// 表情管理システム
// アバターの表情を動的に変更する機能

export class ExpressionManager {
  constructor() {
    this.currentExpression = 'neutral';
    this.expressionQueue = [];
    this.isTransitioning = false;
  }

  // 20代女性受付の表情定義
  static expressions = {
    neutral: {
      eyebrows: 0.1,    // 積極的で友好的な印象
      eyes: 1,          // 明るく開いた目
      mouth: 0.2,       // 微笑みを基本に
      pupils: 0         // 直視で信頼感
    },
    
    happy: {
      eyebrows: 0.3,
      eyes: 0.8,
      mouth: 1,
      pupils: 0
    },
    
    surprised: {
      eyebrows: 0.8,
      eyes: 1.3,
      mouth: 0.5,
      pupils: 0
    },
    
    concerned: {
      eyebrows: -0.5,
      eyes: 0.9,
      mouth: -0.3,
      pupils: 0
    },
    
    attentive: {
      eyebrows: 0.2,
      eyes: 1.1,
      mouth: 0.1,
      pupils: 0
    },
    
    speaking: {
      eyebrows: 0.1,
      eyes: 0.9,
      mouth: 0.3,
      pupils: 0
    },
    
    thinking: {
      eyebrows: -0.2,
      eyes: 0.7,
      mouth: -0.1,
      pupils: -0.3
    },
    
    welcoming: {
      eyebrows: 0.4,
      eyes: 0.9,
      mouth: 0.8,
      pupils: 0
    }
  };

  // 表情を設定
  setExpression(expressionName, duration = 1000) {
    if (!ExpressionManager.expressions[expressionName]) {
      console.warn(`Expression "${expressionName}" not found`);
      return false;
    }

    this.currentExpression = expressionName;
    return ExpressionManager.expressions[expressionName];
  }

  // 表情をキューに追加（連続する表情変化）
  queueExpression(expressionName, duration = 1000, delay = 0) {
    this.expressionQueue.push({
      expression: expressionName,
      duration,
      delay
    });
  }

  // キューの表情を順次実行
  async playExpressionQueue(onExpressionChange) {
    if (this.isTransitioning || this.expressionQueue.length === 0) {
      return;
    }

    this.isTransitioning = true;

    for (const item of this.expressionQueue) {
      if (item.delay > 0) {
        await this.delay(item.delay);
      }

      const expression = this.setExpression(item.expression, item.duration);
      if (expression && onExpressionChange) {
        onExpressionChange(expression);
      }

      await this.delay(item.duration);
    }

    this.expressionQueue = [];
    this.isTransitioning = false;
  }

  // 感情に基づく表情の自動選択
  getExpressionFromEmotion(emotion) {
    const emotionMap = {
      joy: 'happy',
      excitement: 'surprised',
      calm: 'neutral',
      concern: 'concerned',
      focus: 'attentive',
      greeting: 'welcoming',
      processing: 'thinking'
    };

    return emotionMap[emotion] || 'neutral';
  }

  // テキスト内容から感情を推定
  analyzeTextEmotion(text) {
    const positiveWords = ['ありがとう', 'うれしい', 'よろしく', 'こんにちは', 'いらっしゃい'];
    const questionWords = ['？', '?', 'ですか', 'ですが', 'どちら', 'いかが'];
    const concernWords = ['すみません', '申し訳', 'エラー', '問題', '困り'];

    const lowerText = text.toLowerCase();

    if (positiveWords.some(word => lowerText.includes(word))) {
      return 'joy';
    }
    
    if (questionWords.some(word => lowerText.includes(word))) {
      return 'focus';
    }
    
    if (concernWords.some(word => lowerText.includes(word))) {
      return 'concern';
    }

    return 'calm';
  }

  // 複雑な表情シーケンスを作成
  createGreetingSequence() {
    return [
      { expression: 'surprised', duration: 300, delay: 0 },    // 気づく
      { expression: 'welcoming', duration: 800, delay: 100 },  // 笑顔で迎える
      { expression: 'attentive', duration: 1000, delay: 200 }  // 注意深く聞く姿勢
    ];
  }

  createThinkingSequence() {
    return [
      { expression: 'thinking', duration: 500, delay: 0 },     // 考える
      { expression: 'neutral', duration: 300, delay: 200 },    // 一度リセット
      { expression: 'speaking', duration: 800, delay: 100 }    // 答える準備
    ];
  }

  // ユーティリティメソッド
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // メモリクリーンアップ
  destroy() {
    this.expressionQueue = [];
    this.isTransitioning = false;
  }
}

export default ExpressionManager;